import {eventBus} from "../../event-bus";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Alert, Button, Typography, Box, CircularProgress, Checkbox, FormControlLabel, LinearProgress, Paper, TextField} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import {useUser} from "../../providers/user-context";
import {useTranslation} from "react-i18next";
import axiosInstance from "../../config/axios-config";
import i18n from "../../i18n/i18n";
import {useBasket} from "../../providers/basket-context";
import {PageContainer, PageTitle} from "../../components/common/page-container";
import ProductDetailsDialog from "../../dialog/product-details-dialog";
import ConfirmationDialog from "../../dialog/confirmation-dialog";
import {useNavigate} from "react-router-dom";
import GlowingMessage from "../../errors/glowing-message";
import CheckoutDistributorCard, {checkoutTotals, checkoutUnits} from "./checkout-distributor-card";
import CheckoutPermissionControl from "./checkout-permission-control";
import CheckoutPurchaseDialog from "./checkout-purchase-dialog";
import {createCheckoutPurchase} from "./checkout-purchase";

const EMPTY_ROWS = [];

export default function CartTable({navOpen, tenantType}) {
    const {tenantId, userIdentity} = useUser();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {clearBasket, buySelected, updateCountOfBasketItem} = useBasket();
    const permissionScope = `${tenantId}:${userIdentity}`;
    const scope = `${permissionScope}:${i18n.language}`;
    const currentScope = useRef(scope);
    currentScope.current = scope;
    const [snapshot, setSnapshot] = useState({scope, rows: []});
    const rows = snapshot.scope === scope ? snapshot.rows : EMPTY_ROWS;
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [pending, setPending] = useState(false);
    const [message, setMessage] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [removal, setRemoval] = useState(null);
    const [purchase, setPurchase] = useState(null);
    const purchaseRef = useRef(null);
    const request = useRef(null);
    const operationPending = useRef(false);
    const selectionInitialized = useRef(null);
    const [permissions, setPermissions] = useState({scope: permissionScope, choices: {}});
    const choices = permissions.scope === permissionScope ? permissions.choices : {};
    const groups = useMemo(() => {
        const grouped = new Map();
        rows.forEach(row => {
            if (!grouped.has(row.distributorId)) {
                grouped.set(row.distributorId, {id: row.distributorId, name: row.tenantName, rows: []});
            }
            grouped.get(row.distributorId).rows.push(row);
        });
        return [...grouped.values()];
    }, [rows]);
    const selected = useMemo(() => rows.filter(row => selectedRows.includes(row.cartId)), [rows, selectedRows]);
    const suppliers = useMemo(() => groups.filter(group => group.rows.some(row => selectedRows.includes(row.cartId))), [groups, selectedRows]);
    const busy = pending || loading || snapshot.scope !== scope;

    // Late reads/writes belong to the identity that started them, including after unmount.
    useEffect(() => {
        currentScope.current = scope;
        setMessage("");
        setLoadFailed(false);
        purchaseRef.current = null;
        setPurchase(null);
        return () => {currentScope.current = null;};
    }, [scope]);

    useEffect(() => {
        setPermissions(previous => {
            if (previous.scope !== permissionScope) {
                return {scope: permissionScope, choices: {}};
            }
            const retained = Object.fromEntries(Object.entries(previous.choices).filter(([id]) => suppliers.some(group => group.id === id)));
            return Object.keys(retained).length === Object.keys(previous.choices).length ? previous : {scope: permissionScope, choices: retained};
        });
    }, [permissionScope, suppliers]);

    const fetchProducts = useCallback(async search => {
        request.current?.abort();
        // Keep attempted requests for exact retries; an unsubmitted review must follow fresh quotes.
        if (purchaseRef.current && !purchaseRef.current.attempted) {
            purchaseRef.current = null;
            setPurchase(null);
            setMessage(t("CheckoutPurchaseChanged"));
        }
        const controller = new AbortController();
        request.current = controller;
        setLoading(true);
        try {
            const response = await axiosInstance.get("/api/v1/carts/table", {signal: controller.signal,
                params: {searchTerm: search?.length >= 3 ? search : null, page: 0, size: 1000},
                headers: {"Language-Context": i18n.language.toUpperCase() || "EN"}});
            if (controller.signal.aborted || currentScope.current !== scope) {return;}
            const content = response.data.content || [];
            if (content.some(row => !row.cartId || !row.distributorId)) {throw new Error(t("CartLoadFailed"));}
            setSnapshot({scope, rows: content, total: response.data.totalElements ?? content.length});
            const preserveSelection = selectionInitialized.current === scope;
            setSelectedRows(previous => preserveSelection
                ? previous.filter(id => content.some(row => row.cartId === id)) : content.map(row => row.cartId));
            selectionInitialized.current = scope;
            setLoadFailed(false);
        } catch (error) {
            if (!controller.signal.aborted && currentScope.current === scope) {
                setLoadFailed(true);
                setMessage(error.response?.data?.message || t("CartLoadFailed"));
            }
        } finally {
            if (!controller.signal.aborted && currentScope.current === scope) {setLoading(false);}
        }
    }, [scope, t]);

    useEffect(() => {
        request.current?.abort();
        setLoading(true);
        setSelectedProduct(null);
        setRemoval(null);
        const timeout = setTimeout(() => fetchProducts(searchQuery), 500);
        return () => {clearTimeout(timeout); request.current?.abort();};
    }, [searchQuery, fetchProducts]);

    useEffect(() => {
        const refresh = () => fetchProducts(searchQuery);
        eventBus.on("refreshProductsData", refresh);
        return () => eventBus.off("refreshProductsData", refresh);
    }, [searchQuery, fetchProducts]);

    const handleRowSelect = id => setSelectedRows(previous => previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id]);
    const handleGroupSelect = (id, checked) => {
        const ids = groups.find(group => group.id === id).rows.map(row => row.cartId);
        setSelectedRows(previous => checked ? [...new Set([...previous, ...ids])] : previous.filter(value => !ids.includes(value)));
    };

    const handleRemove = async ids => {
        if (operationPending.current || busy || loadFailed || !ids.length) {return;}
        const actionRows = rows.filter(row => ids.includes(row.cartId));
        if (actionRows.length !== ids.length) {return;}
        operationPending.current = true;
        setPending(true);
        try {
            await clearBasket(ids);
            if (currentScope.current !== scope) {return;}
            setMessage(t("RemovedFromCartSuccessfully"));
            await fetchProducts(searchQuery);
        } catch (error) {
            if (currentScope.current === scope) {
                setMessage(error.response?.data?.message || t("CartActionFailed"));
                if (error.response?.status === 409) {await fetchProducts(searchQuery);}
            }
        } finally {
            operationPending.current = false;
            setPending(false);
        }
    };

    const requestPurchase = (ids = selectedRows) => {
        if (operationPending.current || busy || loadFailed || purchaseRef.current || !ids.length) {return;}
        const actionRows = rows.filter(row => ids.includes(row.cartId));
        if (actionRows.length !== ids.length || actionRows.some(row => !selectedRows.includes(row.cartId))) {return;}
        const review = createCheckoutPurchase(scope, actionRows, choices);
        purchaseRef.current = review;
        setPurchase(review);
    };

    const cancelPurchase = () => {
        if (operationPending.current) {return;}
        purchaseRef.current = null;
        setPurchase(null);
    };

    // Keep receipt identity checks beside both outcomes so a late result cannot affect another review.
    const confirmPurchase = async () => {
        const review = purchaseRef.current;
        if (!review || operationPending.current || review.scope !== currentScope.current || (!review.attempted && (busy || loadFailed))) {return;}
        const submission = {...review, attempted: true, error: ""};
        purchaseRef.current = submission;
        setPurchase(submission);
        operationPending.current = true;
        setPending(true);
        try {
            await buySelected(submission.ids, submission.permissions, submission.prices);
            if (currentScope.current === submission.scope && purchaseRef.current === submission) {
                purchaseRef.current = null;
                setPurchase(null);
                navigate("/pharmacy/my-orders");
            }
        } catch (error) {
            if (currentScope.current !== submission.scope || purchaseRef.current !== submission) {return;}
            const errorMessage = error.response?.data?.message || t("CartActionFailed");
            if (error.response?.status === 409) {
                purchaseRef.current = null;
                setPurchase(null);
                setMessage(errorMessage);
                await fetchProducts(searchQuery);
            } else {
                const retry = {...submission, error: errorMessage};
                purchaseRef.current = retry;
                setPurchase(retry);
            }
        } finally {operationPending.current = false; setPending(false);}
    };

    const handleQuantityChange = async (row, quantity) => {
        if (operationPending.current || busy || !Number.isInteger(quantity) || quantity < 1 || quantity === row.cartQuantity) {return;}
        operationPending.current = true;
        setPending(true);
        try {
            // Re-read the shared cart quote before allowing checkout; do not invent an optimistic price.
            await updateCountOfBasketItem(row.cartId, quantity);
            if (currentScope.current === scope) {await fetchProducts(searchQuery);}
        } catch (error) {
            if (currentScope.current === scope) {setMessage(error.response?.data?.message || t("CartActionFailed"));}
        } finally {
            operationPending.current = false;
            setPending(false);
        }
    };

    return <>
        <GlowingMessage message={message} onClose={() => setMessage("")}/>
        <PageContainer navOpen={navOpen} aria-busy={busy}>
            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mb: 2}}>
                <Box sx={{flex: "1 1 240px", minWidth: 0}}><PageTitle component="h1" sx={{mb: 0.5}}>{t("MyCart")}</PageTitle>
                    <Typography variant="body2" color="text.secondary">{t("CheckoutGroupedHint")}</Typography></Box>
                <TextField placeholder={t("Search")} size="small" value={searchQuery} disabled={pending}
                    inputProps={{"aria-label": t("CheckoutSearch")}} onChange={event => setSearchQuery(event.target.value)}
                    sx={{width: {xs: "100%", sm: 280}, maxWidth: "100%"}}/>
            </Box>
            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1}}>
                <FormControlLabel label={t("CheckoutSelectVisible")} control={<Checkbox disabled={busy || !rows.length}
                    checked={rows.length > 0 && selected.length === rows.length} indeterminate={selected.length > 0 && selected.length < rows.length}
                    onChange={event => setSelectedRows(event.target.checked ? rows.map(row => row.cartId) : [])}/>}/>
                <Button color="error" startIcon={<DeleteOutlineIcon/>} disabled={busy || !selected.length}
                    onClick={() => setRemoval({scope, ids: selected.map(row => row.cartId)})}>{t("DeleteSelected")}</Button>
            </Box>
            {loadFailed && <Alert severity="error" sx={{mb: 2}} action={<Button color="inherit" onClick={() => fetchProducts(searchQuery)}>{t("Retry")}</Button>}>{t("CartLoadFailed")}</Alert>}
            {!loading && snapshot.scope === scope && snapshot.total > rows.length && <Alert severity="info" sx={{mb: 2}}>{t("CheckoutMoreResults", {count: rows.length, total: snapshot.total})}</Alert>}
            {/* Reserve refresh feedback space and retain keyed cards to avoid moving the edited row. */}
            <Box sx={{height: 4, mb: 1}}>{loading && rows.length > 0 && <LinearProgress aria-label={t("Loading")}/>}</Box>
            {(loading || snapshot.scope !== scope) && !rows.length ? <Box sx={{display: "flex", justifyContent: "center", p: 6}}><CircularProgress aria-label={t("Loading")}/></Box>
                : rows.length === 0 ? <Paper variant="outlined" sx={{p: 4, textAlign: "center"}}><Typography>{t(searchQuery ? "ProductNotFound" : "EmptyBasket")}</Typography></Paper>
                    : <Box sx={{display: "grid", gap: 3}}>{groups.map(group => <CheckoutDistributorCard key={group.id} group={group}
                        selectedRows={selectedRows} disabled={busy || loadFailed} onSelectGroup={handleGroupSelect} onSelectRow={handleRowSelect}
                        onQuantityChange={handleQuantityChange} onRemove={id => setRemoval({scope, ids: [id]})} onDetails={setSelectedProduct}
                        onBuySelected={requestPurchase}
                        onDeleteSelected={ids => setRemoval({scope, ids, name: group.name || t("Distributor")})}
                        permissionControl={<CheckoutPermissionControl group={group} selected={group.rows.some(row => selectedRows.includes(row.cartId))}
                            checked={choices[group.id] === true} disabled={busy || loadFailed} onChange={checked => setPermissions(previous => ({
                                scope: permissionScope, choices: {...(previous.scope === permissionScope ? previous.choices : {}), [group.id]: checked}
                            }))}/>}/>)}</Box>}
            <Paper variant="outlined" sx={{p: 2, mt: 3, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2}}>
                <Box sx={{flex: "1 1 220px", minWidth: 0}}><Typography component="h2" variant="h6">{t("CheckoutSelectedOrders", {count: suppliers.length})}</Typography>
                    <Typography variant="body2" color="text.secondary">{t("CheckoutSelectedCounts", {lines: selected.length, units: checkoutUnits(selected)})}</Typography>
                    <Typography variant="caption" color="text.secondary">{t("CheckoutSubtotalHint")}</Typography></Box>
                <Box sx={{minWidth: 0}}><Typography variant="caption" color="text.secondary">{t("CheckoutSelectedSubtotal")}</Typography>
                    <Typography variant="h6">{selected.length ? checkoutTotals(selected) : "—"}</Typography></Box>
                <Button variant="contained" startIcon={pending ? <CircularProgress size={18} color="inherit"/> : <ShoppingCartCheckoutIcon/>}
                    disabled={busy || loadFailed || !selected.length} onClick={() => requestPurchase()} sx={{width: {xs: "100%", sm: "auto"}}}>{t("BuySelected")}</Button>
            </Paper>
            {snapshot.scope === scope && <ProductDetailsDialog open={Boolean(selectedProduct)} onClose={() => setSelectedProduct(null)} product={selectedProduct} tenantType={tenantType} readOnly/>}
            <ConfirmationDialog open={Boolean(removal && removal.scope === scope)} title={t("CheckoutRemoveTitle")}
                message={t(removal?.name ? "CheckoutRemoveDistributorMessage" : "CheckoutRemoveMessage", {count: removal?.ids.length || 0, name: removal?.name})}
                onCancel={() => setRemoval(null)} onConfirm={() => {const ids = removal.ids; setRemoval(null); handleRemove(ids);}}/>
            <CheckoutPurchaseDialog purchase={purchase?.scope === scope ? purchase : null} pending={pending}
                onCancel={cancelPurchase} onConfirm={confirmPurchase}/>
        </PageContainer>
    </>;
}
